import React,{ useState, useEffect } from 'react'
import clsx from 'clsx'
import { useNavigate } from 'react-router-dom'
import { PasswordMeterComponent } from '../../../../_metronic/assets/ts/components'
import { acceptInvitation, decodeInvitation } from '../../../services/invitation'
import { userLookup } from '../../../services/user-lookup'
import { usePublicPlans } from '../../../hooks'
import { formatPlanOptionLabel } from '../../../helper-functions/planTier'
import { PlanFeaturesCard } from '../../../components/Tables/PlanFeaturesCard'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import CustomAlert from '../../../components/CustomAlert'
import { useTheme } from '../../../context/ThemeContext'
import { toAbsoluteUrl } from '../../../../_metronic/helpers'
import { getAuth } from '../core/AuthHelpers'
export function Invitation(): React.JSX.Element {
  const {theme} =useTheme()
  const [decodedDataState, setDecodedDataState] = useState({})
  const [decodedSignupData, setDecodedSignupData] = useState<any>({})
  const [isLoading, setIsLoading] = useState(true)
  const [redirectToRegister, setRedirectToRegister] = useState(false)
  const [invitationErrorMsg, setInvitationErrorMsg] = useState("")
  const [decodedEmail, setDecodedEmail] = useState("")
  // Only new (non-guest) invitees see the plan picker, so defer the fetch until
  // the token has decoded and that branch is known — guest/existing-user
  // accepts never trigger the round-trip.
  const isNewInvitee = redirectToRegister && !decodedSignupData?.role?.isGuest
  const { plans, plansLoading, plansError, selectedPlanId, setSelectedPlanId, selectedPlan } =
    usePublicPlans(isNewInvitee)
  const urlAddress = window.location.search
  const token = urlAddress.slice(7)
  const authData:any = getAuth();
  const navigate = useNavigate()
  const acceptInvitationHandler = async () => {
    // Land the invitee on the board they accepted, not a blank /home. Falls
    // back to "Shared with me" if the token had no boardId — both live under
    // the /board/* routes, a bare /other-boards hits the 404 catch-all.
    const acceptedBoardPath = decodedSignupData?.boardId
      ? `/board/${decodedSignupData.boardId}`
      : '/board/other-boards'
    if (redirectToRegister === false) {
      try {
        setIsLoading(true)
        await acceptInvitation(decodedDataState)
        if (authData) {
          setTimeout(() => {
            navigate(acceptedBoardPath)
          }, 1000)
        } else {
          // Not logged in: stash the destination so Login's
          // consumePostLoginRedirect lands them there after they authenticate.
          sessionStorage.setItem('postLoginRedirect', acceptedBoardPath)
          setTimeout(() => {
            navigate('/auth')
            setIsLoading(false)
          }, 3000)
        }
      } catch (error: any) {
        const msg = error.response.data.message
        const msgTrim = msg.trim()
        if (msgTrim === 'User is already member of this board') {
          setInvitationErrorMsg("Invitation Already Accepted!")
          setTimeout(() => {
            navigate(acceptedBoardPath)
            setIsLoading(false)
          }, 5000)
        }else{
          setInvitationErrorMsg(msgTrim)
          setTimeout(() => {
            navigate('/auth')
            setIsLoading(false)
          }, 5000)
        }
        setIsLoading(false)
        return error
      }
      // finally{
      //     setIsLoading(false)
      //   }
    } else {
      const isGuest = decodedSignupData.role.isGuest || false
      if(isGuest){
        setTimeout(() => {
          navigate(`/auth/${token}/guest-invitation`,{state:{decodedSignupData:decodedSignupData}})
        }, 2000)
      }else{
        setTimeout(() => {
          navigate(`/auth/${token}/registration`,{state:{decodedDataName: decodedSignupData?.receiverName, decodedDataEmail: decodedEmail, decodedSignupData: decodedSignupData, selectedPlanId: selectedPlan?.id, selectedPlanName: selectedPlan?.name}})
        }, 2000)
      }
    }
  }
  const decodeTokenHandler = async () => {
    try {
      const decodedResponse = await decodeInvitation({ token, usage: 'boardInvite' })
      const decodedData = decodedResponse.data.decodeToken
      setDecodedEmail(decodedData.email)
      setDecodedSignupData(decodedData)
      try {
        const lookupResponse = await userLookup(decodedData.email)
        const payload = {
          email: decodedData.email,
          boardId: decodedData.boardId,
          roleId: decodedData.roleId,
          userId: lookupResponse.data.user.id,
          isAdmin: decodedData.role.isAdmin,
          inviteByUserId:decodedData.inviteByUserId
          
        }
        setDecodedDataState(payload)
      } catch (error: any) {
        if (error.response.status === 404) {
          setRedirectToRegister(true)
        }
        return error
      }
    } catch (error: any) {
      const msg = error.response.data.message
      if (msg === "jwt expired") {
        navigate('/error/404')
      }
    }finally{
      setIsLoading(false)
    }
  }
  useEffect(() => {
    PasswordMeterComponent.bootstrap()
    decodeTokenHandler()
    //eslint-disable-next-line
  }, [])

  // react-loading-skeleton defaults to light grey, which is invisible-but-harsh
  // on the dark theme — give it theme-matched base/highlight colours.
  const skeletonBase = theme === 'dark' ? '#2b2b40' : '#e6e6e6'
  const skeletonHighlight = theme === 'dark' ? '#323248' : '#f5f5f5'

  // Dropdown + features-card placeholder. Mirrors the real layout so nothing
  // jumps when plans resolve.
  const plansFieldsSkeleton = (
    <SkeletonTheme baseColor={skeletonBase} highlightColor={skeletonHighlight}>
      <Skeleton height={48} />
      <div className={`card mt-4 shadow-none  ${theme === 'dark' ? 'theme__div':''}`}>
        <div className='card-header border-0 pt-5'>
          <Skeleton height={22} width={170} />
          <div className='mt-2'>
            <Skeleton height={14} width={120} />
          </div>
        </div>
        <div className={`card-body py-5 `}>
          <Skeleton count={6} height={26} className='mb-3' />
        </div>
      </div>
    </SkeletonTheme>
  )

  const plansPanelSkeleton = (
    <SkeletonTheme baseColor={skeletonBase} highlightColor={skeletonHighlight}>
      <div className='mx-auto mb-8 text-start' style={{ maxWidth: 480 }}>
        <div className='mb-3'>
          <Skeleton height={24} width={150} />
        </div>
        {plansFieldsSkeleton}
      </div>
    </SkeletonTheme>
  )

  return (
    <>
      <div className='mb-10 text-center'>
      <div className='d-flex align-items-center justify-content-center gap-5'>
        <h1 className={` mb-3 ${theme === 'dark' ? 'white__text' :'text-dark'} mt-7`}>Accept Invitation</h1>
 <a href={process.env.REACT_APP_B4IGO_LANDING_PAGE} className=''>
          <img
            alt='Logo'
            src={toAbsoluteUrl('/media/logos/b4igo-go.png')}
            className='h-100px w-90px my-5 me-15'
          />
        </a>
      </div>
      </div>
      <div className='text-center'>
        {/* While the invite token is still decoding we don't yet know if the
            user is a new invitee, so show the plan skeleton until it responds. */}
        {isLoading && (
          <>
            {plansPanelSkeleton}
            <SkeletonTheme baseColor={skeletonBase} highlightColor={skeletonHighlight}>
              <Skeleton count={1} height={35} width={220} />
            </SkeletonTheme>
          </>
        )}

        {/* New invitees sign up after accepting, so let them pick a plan first —
            the choice carries into the registration form. Existing users and
            guest invites skip this and just accept. */}
        {!isLoading && isNewInvitee && (
          <div className='mx-auto mb-8 text-start' style={{ maxWidth: 480 }}>
            <label className={`form-label fw-bold fs-5 mb-3 ${theme === 'dark' ? 'white__text' : 'text-dark'}`}>
              Choose your plan
            </label>
            {plansLoading ? (
              plansFieldsSkeleton
            ) : plansError ? (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert' className='text-danger'>{plansError}</span>
                </div>
              </div>
            ) : (
              <>
                <select
                  value={selectedPlanId}
                  onChange={(e) => setSelectedPlanId(e.target.value)}
                  className={clsx(
                    `form-select form-select-lg form-control-solid ${theme === 'dark' ? 'dark__theme__input' : ''}`,
                    { 'is-invalid': !selectedPlanId }
                  )}
                >
                  <option value='' disabled>
                    Select a plan
                  </option>
                  {plans.map((plan: any) => (
                    <option key={plan.id} value={plan.id}>
                      {formatPlanOptionLabel(plan)}
                    </option>
                  ))}
                </select>
                {selectedPlan && (
                  <PlanFeaturesCard
                    plan={selectedPlan}
                    variant='list'
                    className='mt-4 shadow-none border'
                  />
                )}
              </>
            )}
          </div>
        )}

        {!isLoading && (
          <button
            type='button'
            className='btn btn-lg btn-primary w-50 mb-5'
            onClick={acceptInvitationHandler}
            disabled={isNewInvitee && (plansLoading || !selectedPlanId)}
          >
            Accept
          </button>
        )}
        <CustomAlert variant={'info'} alertMessage={invitationErrorMsg} setAlertMessage={setInvitationErrorMsg} />
      </div>
    </>
  )
}